import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Input, Select, Table, Tooltip } from 'antd';
import { CheckOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { PencilIcon } from '@/assets/icons/icons';
import { useSpin } from '../../../components/SpinContext';
import { truncateByLength, useToken } from '../../../common/helper';
import shipmentCount from '../../../assets/images/shipmentCount.svg';
import './BulkUploadEditor.css';
import { CustomModalWithoutButton, CustomTitle } from '../../../components';
import { prepareCleanedPayload } from './utils';
import { useBulkUploadData } from './useBulkUploadData';
import BulkUploadFooter from './BulkUploadEditorFooter';
import ErrorAlert from './ErrorAlert';
import BulkFieldSelector from './BulkFieldSelector';
import theme from '@/assets/theme';
import { themeT } from '@/utils/themeText';

const SENSOR_SLOT_PROVIDER_NAME_RE = /^sensor_slot_\d+_name$/;

/** Prevents ``scroll.y`` toggling undefined ↔ number when content height ≈ viewport (ResizeObserver feedback loop). */
const BULK_TABLE_SCROLL_Y_HYSTERESIS_PX = 32;

/** Must match ``margin-bottom`` on ``.bulk-upload-editor-main`` in ``BulkUploadEditor.css`` (table ↔ footer spacing). */
const BULK_TABLE_ABOVE_FOOTER_GAP_PX = 12;

/** Keep dropdowns/tooltips under the modal so they unmount with it (body portaling caused lingering UI after close). */
function bulkUploadPopupContainer(node) {
  if (!node || typeof document === 'undefined') {
    return document.body;
  }
  return (
    node.closest('.ant-modal-content') ??
    node.closest('.ant-modal-wrap') ??
    document.body
  );
}

/**
 * Mount cell validation error tooltips inside the table's scroll layer so they
 * move with the row and are clipped by ``overflow`` at the table edges (no
 * overlay on the sticky header or footer when scrolling vertically).
 */
// function bulkUploadErrorTooltipContainer(node) {
//   if (!node || typeof document === 'undefined') {
//     return document.body;
//   }
//   return (
//     node.closest('.ant-table-body') ??
//     node.closest('.ant-table-content') ??
//     bulkUploadPopupContainer(node)
//   );
// }

function isSensorSlotProviderNameKey(key) {
  return typeof key === 'string' && SENSOR_SLOT_PROVIDER_NAME_RE.test(key);
}

function sensorSlotTrackingFieldKey(providerKey) {
  const m = providerKey.match(/^sensor_slot_(\d+)_name$/);
  return m ? `sensor_slot_${m[1]}_tracking_id` : null;
}

/** Long free-text cells: multi-line textarea instead of single-line input. */
function isBulkWrapTextFieldKey(key) {
  return (
    key === 'reference_number' ||
    key === 'courier_tracking_id' ||
    key === 'sensor_tracking_id' ||
    /^sensor_slot_\d+_tracking_id$/.test(key)
  );
}

/** Wrap-text columns: flex layout lives here so ``.bulk-upload-cell-align`` can stay table-centered. */
function BulkUploadWrapCellOuter({ active, children }) {
  if (!active) return children;
  return <div className='bulk-upload-wrap-cell-outer'>{children}</div>;
}

/** Match legacy ``sensor`` column: map API string / UUID / {label,value} to picker shape. */
function transformSensorProviderPickerValue(fieldValue, sensorsList) {
  if (!Array.isArray(sensorsList) || sensorsList.length === 0) {
    return fieldValue;
  }
  if (
    fieldValue &&
    typeof fieldValue === 'object' &&
    Object.prototype.hasOwnProperty.call(fieldValue, 'value') &&
    fieldValue.value != null &&
    fieldValue.value !== ''
  ) {
    const item = sensorsList.find((s) => s.sensor_id === fieldValue.value);
    return {
      label: item?.sensor_name || fieldValue.label,
      value: fieldValue.value,
    };
  }
  if (fieldValue == null || fieldValue === '') {
    return fieldValue;
  }
  const byId = sensorsList.find((s) => s.sensor_id === fieldValue);
  if (byId) {
    return { label: byId.sensor_name, value: byId.sensor_id };
  }
  const rawStr = String(fieldValue).trim();
  const byName = sensorsList.find(
    (s) => (s.sensor_name || '').toLowerCase() === rawStr.toLowerCase()
  );
  if (byName) {
    return { label: byName.sensor_name, value: byName.sensor_id };
  }
  return { label: rawStr, value: fieldValue };
}

export default function BulkUploadEditor({
  onCancel,
  fileData,
  unauthorize,
  onBackToStep1,
}) {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingFieldKey, setEditingFieldKey] = useState(null);
  const [bulkFieldValue, setBulkFieldValue] = useState(null);
  const { token } = useToken();
  const [currentErrorCellIndex, setCurrentErrorCellIndex] = useState(0);
  const { showSpin, hideSpin } = useSpin();

  /* Dynamic ``scroll.y`` for the data table.

     We previously used a ``calc(100vh - Npx)`` constant, which was fragile:
     ``N`` had to account for the modal margin, body padding, title, the
     conditionally-mounted ErrorAlert (~66px), the table header, and the
     two-row footer. Off-by-30px+ errors here were directly visible as either
     dead white space below the table *or* the table bleeding through the
     footer.

     ``.bulk-upload-editor-main`` uses ``flex: 0 1 auto`` (see CSS) so it
     shrinks to the table's natural height when the dataset is small. The
     effect measures ``.bulk-upload-editor`` (root) height and subtracts the
     title, footer, optional alert, and table header to get ``availableBodyH``.
     When rendered row height fits, ``scroll.y`` is ``undefined`` so Antd does
     not force a tall scroll body; when rows overflow, ``scroll.y`` is set to
     ``availableBodyH`` so the body scrolls inside the modal. */
  const mainContainerRef = useRef(null);
  /** Ref to the open error tooltip (focused error cell) so we can ``forceAlign`` on table scroll. */
  const activeErrorTooltipRef = useRef(null);
  /** Ref to the DOM element of the currently-focused error cell shell. */
  const focusedCellElRef = useRef(null);
  /**
   * Running maximum of the editor root's clientHeight across all recalc calls.
   * Used to floor ``rootH`` so that fixing errors (which shrinks rows and may
   * collapse the modal body whose height is ``auto``) cannot trigger a
   * shrinking feedback loop where each ResizeObserver tick produces a smaller
   * ``availableBodyH`` and a smaller ``tableScrollY``.
   */
  const peakRootHRef = useRef(0);
  // ``undefined`` = no fixed scroll height (table auto-sizes); number = scrollable body
  const [tableScrollY, setTableScrollY] = useState(undefined);
  /**
   * Whether the focused error cell is currently scrolled into the visible
   * portion of the table body.  When ``false`` the error tooltip is hidden to
   * prevent it from floating at the wrong position.
   */
  const [_focusedCellVisible, setFocusedCellVisible] = useState(true);

  const alignActiveErrorTooltip = useCallback(() => {
    const tip = activeErrorTooltipRef.current;
    if (!tip) return;
    if (typeof tip.forceAlign === 'function') {
      tip.forceAlign();
    } else if (typeof tip.forcePopupAlign === 'function') {
      tip.forcePopupAlign();
    }
  }, []);

  /**
   * Re-evaluate whether the focused cell element is inside the table scroll
   * viewport.  Called on every table scroll event.
   */
  const checkFocusedCellVisibility = useCallback(() => {
    const cellEl = focusedCellElRef.current;
    const main = mainContainerRef.current;
    if (!cellEl || !main) {
      setFocusedCellVisible(true);
      return;
    }
    const scrollContainer =
      main.querySelector('.ant-table-body') ??
      main.querySelector('.ant-table-content');
    if (!scrollContainer) {
      setFocusedCellVisible(true);
      return;
    }
    const containerRect = scrollContainer.getBoundingClientRect();
    const cellRect = cellEl.getBoundingClientRect();
    // Cell is "visible" if it overlaps at least partially with the scroll container viewport.
    const visible =
      cellRect.bottom > containerRect.top &&
      cellRect.top < containerRect.bottom;
    setFocusedCellVisible(visible);
  }, []);

  useEffect(() => {
    const main = mainContainerRef.current;
    if (!main || typeof ResizeObserver === 'undefined') return;
    /* The editor root (.bulk-upload-editor) is the flex column that owns the
       full modal body height. We measure the available table-body height from
       there so that ``main``'s own shrink-to-fit sizing (flex: 0 1 auto)
       doesn't cause ``availableBodyH`` to collapse when content is small. */
    const root = main.closest('.bulk-upload-editor') ?? main.parentElement;

    let rafId = 0;
    const recalc = () => {
      const rawRootH = root?.clientHeight ?? main.clientHeight;
      /* Track the running maximum so the floor only ever moves upward.
         This prevents the modal (height:auto) from triggering a shrinking
         feedback loop when errors are fixed and rows become shorter. */
      if (rawRootH > peakRootHRef.current) {
        peakRootHRef.current = rawRootH;
      }
      const rootH = peakRootHRef.current || rawRootH;
      const titleH =
        root?.querySelector(':scope > .ant-typography')?.offsetHeight ?? 0;
      const footerH =
        root?.querySelector(':scope > :last-child')?.offsetHeight ?? 0;
      const alertH = main.querySelector('.ant-alert')?.offsetHeight ?? 0;
      const headerH = main.querySelector('.ant-table-thead')?.offsetHeight ?? 0;
      const buffer = 8;
      /* The max body height is the modal interior minus everything outside
         the table body: title, footer, alert banner, table header. */
      const availableBodyH = Math.max(
        60,
        rootH -
          titleH -
          footerH -
          alertH -
          headerH -
          buffer -
          BULK_TABLE_ABOVE_FOOTER_GAP_PX
      );
      /* When the rows fit, pass ``undefined`` so Antd renders the table
         without a fixed-height scroll container (no white gap below short
         datasets). Only set a numeric value when rows overflow, so the
         scrollbar is needed. */
      /* When scroll.y is undefined Ant renders rows in ``.ant-table-content table``;
         when scroll.y is a number it uses ``.ant-table-body table``.
         Always query both so naturalH is never stuck at 0. */
      const innerTable =
        main.querySelector('.ant-table-body table') ??
        main.querySelector('.ant-table-content table');
      const naturalH = innerTable?.offsetHeight ?? 0;
      const band = BULK_TABLE_SCROLL_Y_HYSTERESIS_PX;

      setTableScrollY((prev) => {
        if (naturalH <= 0) {
          return typeof prev === 'number' ? availableBodyH : prev;
        }
        /* From auto-height: only enable inner scroll when content clearly exceeds the slot. */
        if (prev === undefined) {
          const next =
            naturalH > availableBodyH + band ? availableBodyH : undefined;
          return next;
        }
        /* From fixed ``scroll.y``: only drop inner scroll when content clearly fits (avoids flip-flop). */
        if (typeof prev === 'number') {
          if (naturalH < availableBodyH - band) {
            return undefined;
          }
          return availableBodyH;
        }
        return prev;
      });
    };
    const scheduleRecalc = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        recalc();
      });
    };

    recalc();
    const target = root ?? main;
    const ro = new ResizeObserver(scheduleRecalc);
    ro.observe(target);
    /* Also observe ``main`` so that when the table renders its rows (which
       changes ``main``'s scroll height even though ``root`` is fixed by the
       modal) we still re-run recalc and can enable ``scroll.y``. */
    if (target !== main) ro.observe(main);
    /* Only watch ``main``'s direct children: catches the ErrorAlert
       mount/unmount without firing on every keystroke inside the table. */
    const mo = new MutationObserver(scheduleRecalc);
    mo.observe(main, { childList: true });
    window.addEventListener('resize', scheduleRecalc);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', scheduleRecalc);
    };
  }, []);

  useEffect(() => {
    return () => {
      const ae = document.activeElement;
      if (ae instanceof HTMLElement) {
        ae.blur();
      }
    };
  }, []);

  useEffect(() => {
    const main = mainContainerRef.current;
    if (!main) {
      return undefined;
    }

    const scrollTargets = [
      main.querySelector('.ant-table-body'),
      main.querySelector('.ant-table-content'),
    ].filter(Boolean);

    const onScroll = () => {
      checkFocusedCellVisibility();
      alignActiveErrorTooltip();
    };

    scrollTargets.forEach((el) =>
      el.addEventListener('scroll', onScroll, { passive: true })
    );
    return () => {
      scrollTargets.forEach((el) => el.removeEventListener('scroll', onScroll));
    };
  }, [alignActiveErrorTooltip, checkFocusedCellVisibility, tableScrollY]);

  useLayoutEffect(() => {
    // When the focused cell changes (navigation or new focus), assume it is
    // visible (goToError scrolls it into view) and realign the tooltip.
    setFocusedCellVisible(true);
    alignActiveErrorTooltip();
  }, [currentErrorCellIndex, alignActiveErrorTooltip, tableScrollY]);

  const {
    data,
    setData,
    couriersList,
    sensorsList,
    locationList,
    productList,
    allTeamsUnfiltered,
    allTeams,
  } = useBulkUploadData(token, fileData);

  const hasOnlyDefaultTeam =
    Array.isArray(allTeamsUnfiltered) && allTeamsUnfiltered.length === 1;
  const hasDefaultTeamFallback =
    Array.isArray(allTeamsUnfiltered) &&
    allTeamsUnfiltered.some(
      (t) =>
        String(t?.name || '')
          .trim()
          .toLowerCase() === 'default'
    );
  const allowEmptyTeamsByFallback =
    hasOnlyDefaultTeam || hasDefaultTeamFallback;

  const validateField = useCallback(
    (fieldKey, value) => {
      if (fieldKey === 'reference_number') {
        return !value || String(value).trim() === ''
          ? 'Reference number is required'
          : '';
      }

      if (fieldKey === 'teams') {
        if (allowEmptyTeamsByFallback) return '';

        const isEmpty =
          !value ||
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === 'string' && value.trim() === '');

        return isEmpty
          ? 'Teams can not be empty when company teams exist!'
          : '';
      }

      return '';
    },
    [allowEmptyTeamsByFallback]
  );

  const resolveTeam = (rawValue, label) => {
    const teamId =
      rawValue &&
      typeof rawValue === 'object' &&
      !Array.isArray(rawValue) &&
      Object.prototype.hasOwnProperty.call(rawValue, 'value')
        ? rawValue.value
        : rawValue;
    const team = allTeamsUnfiltered.find(
      (t) => String(t.id) === String(teamId ?? '')
    );
    const name =
      team?.name ??
      (typeof label === 'string' || typeof label === 'number'
        ? String(label)
        : teamId != null
          ? String(teamId)
          : '');
    return { team, name };
  };

  const teamsMaxTagPlaceholder = (omittedValues) => (
    <Tooltip
      title={omittedValues
        .map(({ value, label }) => resolveTeam(value, label).name)
        .join(', ')}
    >
      <div className='bulk-upload-team-max-tag'>
        + {omittedValues.length} ...
      </div>
    </Tooltip>
  );

  const customTagRender = (props) => {
    const { value: rawValue, label, closable, onClose, isMaxTag } = props;

    if (isMaxTag) {
      return <>{label}</>;
    }

    const { team, name: displayValue } = resolveTeam(rawValue, label);

    return (
      <div className='bulk-upload-team-tag'>
        <span
          className='bulk-upload-team-tag-dot'
          // eslint-disable-next-line react/forbid-dom-props -- the dot's colour is the team's colour, known only at run time
          style={{ backgroundColor: team?.color || 'var(--text-primary)' }}
        />
        <Tooltip title={displayValue}>
          <span className='bulk-upload-team-tag-name'>
            {truncateByLength(displayValue, 12)}
          </span>
        </Tooltip>
        {closable && (
          <span className='bulk-upload-team-tag-close' onClick={onClose}>
            ×
          </span>
        )}
      </div>
    );
  };

  const inputRefs = useRef({});

  const errorCells = data.flatMap((row, rowIndex) =>
    Object.entries(row)
      .filter(([_, cell]) => typeof cell === 'object' && cell.error)
      .map(([fieldKey]) => ({ rowIndex, fieldKey }))
  );

  const getErrorCount = () =>
    data.reduce(
      (count, row) =>
        count +
        Object.values(row).filter(
          (f) =>
            typeof f === 'object' &&
            typeof f?.error === 'string' &&
            f.error.trim() !== ''
        ).length,
      0
    );

  const goToError = (direction) => {
    const newIndex =
      direction === 'up'
        ? Math.max(currentErrorCellIndex - 1, 0)
        : Math.min(currentErrorCellIndex + 1, errorCells.length - 1);

    const { rowIndex, fieldKey } = errorCells[newIndex];
    setCurrentErrorCellIndex(newIndex);

    setTimeout(() => {
      const targetRef = inputRefs.current?.[`${rowIndex}-${fieldKey}`];

      if (targetRef) {
        if (typeof targetRef.focus === 'function') {
          targetRef.focus();
        }

        if (typeof targetRef.scrollIntoView === 'function') {
          targetRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 0);
  };

  const isRowValid = (row) =>
    Object.values(row).every(
      (field) =>
        typeof field !== 'object' || !field.error || field.error.trim() === ''
    );

  const handleEditField = (fieldKey) => {
    setEditingFieldKey(fieldKey);
    setEditModalOpen(true);
    setBulkFieldValue();
  };

  const handleSave = () => {
    const trackingFieldKey =
      editingFieldKey === 'sensor'
        ? 'sensor_tracking_id'
        : isSensorSlotProviderNameKey(editingFieldKey)
          ? sensorSlotTrackingFieldKey(editingFieldKey)
          : null;

    const providerValueHasSelection = (pv) => {
      if (pv == null) return false;
      if (
        typeof pv === 'object' &&
        !Array.isArray(pv) &&
        Object.prototype.hasOwnProperty.call(pv, 'value')
      ) {
        return pv.value != null && String(pv.value).trim() !== '';
      }
      return String(pv).trim() !== '';
    };

    const updated = data.map((item) => {
      const nextCell = {
        ...item[editingFieldKey],
        value: bulkFieldValue,
        error: validateField(editingFieldKey, bulkFieldValue),
      };

      let next = {
        ...item,
        [editingFieldKey]: nextCell,
      };

      if (trackingFieldKey && item[trackingFieldKey] != null) {
        const raw = next[trackingFieldKey]?.value;
        const sensorId =
          typeof raw === 'string'
            ? raw.trim()
            : raw != null
              ? String(raw).trim()
              : '';
        const hasProvider = providerValueHasSelection(nextCell.value);
        next = {
          ...next,
          [trackingFieldKey]: {
            ...next[trackingFieldKey],
            error:
              hasProvider && !sensorId
                ? 'Sensor ID is required when Sensor Provider is set.'
                : '',
          },
        };
      }

      return next;
    });

    setData(updated);
    setEditModalOpen(false);
  };

  useEffect(() => {
    if (!fileData?.data) return;

    const updated = fileData.data.map((row) => {
      const transformField = (
        fieldValue,
        list,
        keyLabel = 'name',
        keyValue = 'id'
      ) => {
        if (!Array.isArray(list) || list.length === 0) return fieldValue;

        if (Array.isArray(fieldValue)) {
          return fieldValue.map((val) => {
            if (
              val &&
              typeof val === 'object' &&
              Object.prototype.hasOwnProperty.call(val, 'value')
            ) {
              return {
                label: val.label || val.value,
                value: val.value,
              };
            }
            const item = list.find((i) => i[keyValue] === val);
            return { label: item?.[keyLabel] || val, value: val };
          });
        } else {
          const item = list.find((i) => i[keyValue] === fieldValue);
          return { label: item?.[keyLabel] || fieldValue, value: fieldValue };
        }
      };

      const transformedTeams = transformField(
        row.teams?.value || [],
        allTeamsUnfiltered,
        'name',
        'id'
      );
      const referenceValue = row.reference_number?.value || '';

      const sensorSlotProviderCells = {};
      Object.keys(row).forEach((fieldKey) => {
        if (fieldKey === 'sensor' || !isSensorSlotProviderNameKey(fieldKey)) {
          return;
        }
        const cell = row[fieldKey];
        if (cell && typeof cell === 'object' && 'value' in cell) {
          sensorSlotProviderCells[fieldKey] = {
            ...cell,
            value: transformSensorProviderPickerValue(cell.value, sensorsList),
          };
        }
      });

      return {
        ...row,
        ...sensorSlotProviderCells,
        teams: {
          ...row.teams,
          value: transformedTeams,
          error:
            row.teams?.error &&
            row.teams.error !==
              'Teams can not be empty when company teams exist!'
              ? row.teams.error
              : validateField('teams', transformedTeams),
        },
        courier: {
          ...row.courier,
          value: transformField(
            row.courier?.value,
            couriersList,
            'courier_name',
            'courier_id'
          ),
        },
        sensor: {
          ...row.sensor,
          value: transformSensorProviderPickerValue(
            row.sensor?.value,
            sensorsList
          ),
        },
        products: {
          ...row.products,
          value: transformField(
            row.products?.value || [],
            productList,
            'name',
            'id'
          ),
        },
        from_location: {
          ...row.from_location,
          value: transformField(
            row.from_location?.value,
            locationList,
            'name',
            'id'
          ),
        },
        to_location: {
          ...row.to_location,
          value: transformField(
            row.to_location?.value,
            locationList,
            'name',
            'id'
          ),
        },
        reference_number: {
          ...row.reference_number,
          value: referenceValue,
          error:
            row.reference_number?.error &&
            row.reference_number.error !== 'Reference number is required'
              ? row.reference_number.error
              : validateField('reference_number', referenceValue),
        },
      };
    });

    setData(updated);
  }, [
    allTeamsUnfiltered,
    couriersList,
    sensorsList,
    productList,
    locationList,
    fileData.data,
    setData,
    validateField,
  ]);

  const columns = [
    {
      title: '',
      dataIndex: 'statusIcon',
      key: 'statusIcon',
      width: 40,
      onCell: () => ({
        style: {
          padding: '0 4px',
        },
      }),
      render: (_, record) => {
        const hasError = Object.values(record).some(
          (field) =>
            typeof field === 'object' &&
            typeof field?.error === 'string' &&
            field.error.trim() !== ''
        );

        return (
          <div className='bulk-upload-cell-align'>
            {hasError ? (
              <ExclamationCircleFilled
                style={{ color: 'var(--danger)', fontSize: 16 }}
              />
            ) : (
              <CheckOutlined
                style={{ color: 'var(--gray-600)', fontSize: 16 }}
              />
            )}
          </div>
        );
      },
    },
    ...(fileData?.header || []).map(({ key, value }) => {
      const showAsterisk =
        key === 'reference_number' ||
        key === 'courier' ||
        key === 'courier_tracking_id' ||
        // key === 'products' ||
        key === 'teams';

      return {
        title: (
          <div className='bulk-upload-header-title'>
            <span className='bulk-upload-header-label'>
              {value}
              {showAsterisk && (
                <span className='bulk-upload-header-required'>*</span>
              )}
            </span>
            {!['reference_number', 'courier_tracking_id'].includes(key) &&
              !['from_location_address', 'to_location_address'].includes(
                key
              ) && (
                <button
                  type='button'
                  aria-label='Edit'
                  onClick={() => handleEditField(key)}
                  className='bulk-upload-header-edit'
                >
                  <PencilIcon width={14} height={14} />
                </button>
              )}
          </div>
        ),
        dataIndex: key,
        key,
        ...(key === 'teams'
          ? {
              width: 240,
              onCell: () => ({
                style: {
                  whiteSpace: 'normal',
                  width: 240,
                },
              }),
            }
          : key === 'products'
            ? {
                width: 180,
                onCell: () => ({
                  className: 'bulk-upload-cell--products',
                  style: {
                    whiteSpace: 'normal',
                  },
                }),
              }
            : ['from_location_address', 'to_location_address'].includes(key)
              ? {
                  width: 200,
                  onHeaderCell: () => ({
                    className: 'bulk-upload-header--location',
                  }),
                  onCell: () => ({
                    className: 'bulk-upload-cell--location-address',
                    style: {
                      whiteSpace: 'normal',
                    },
                  }),
                }
              : ['from_location', 'to_location'].includes(key)
                ? {
                    width: 200,
                    onHeaderCell: () => ({
                      className: 'bulk-upload-header--location',
                    }),
                    onCell: () => ({
                      className: 'bulk-upload-cell--location',
                      style: {
                        whiteSpace: 'normal',
                      },
                    }),
                  }
                : isBulkWrapTextFieldKey(key)
                  ? {
                      width: 180,
                      onCell: () => ({
                        className: 'bulk-upload-cell--wrap-text',
                        style: {
                          whiteSpace: 'normal',
                        },
                      }),
                    }
                  : {
                      width: 180,
                    }),
        render: (field, record, rowIndex) => {
          const inputKey = `${rowIndex}-${key}`;
          const isFocused =
            currentErrorCellIndex !== null &&
            errorCells[currentErrorCellIndex]?.rowIndex === rowIndex &&
            errorCells[currentErrorCellIndex]?.fieldKey === key;

          const hasError =
            typeof field?.error === 'string' && field.error.trim() !== '';
          const className = `bulk-cell-shell ${hasError ? 'error-cell' : ''} ${
            isFocused ? 'focus-error' : ''
          }`;

          const isLocAddrCol =
            key === 'from_location_address' || key === 'to_location_address';
          const isWrapTextCol = isBulkWrapTextFieldKey(key);

          if (isLocAddrCol) {
            const addrText = String(field?.value ?? '').trim();
            return (
              <div
                className={`bulk-upload-cell-align bulk-upload-cell-align--field ${
                  isFocused && hasError ? 'bulk-upload-cell-align--raised' : ''
                }`}
              >
                <div
                  className={`${className} bulk-cell-shell--location-address`}
                >
                  <div
                    className='bulk-upload-line-clamp-2 bulk-upload-line-clamp-2--address'
                    title={addrText || undefined}
                  >
                    {addrText}
                  </div>
                </div>
              </div>
            );
          }

          const selectCommon = {
            size: 'small',
            value: field?.value,
            style: { width: '100%' },
          };

          const selectOptions =
            key === 'courier'
              ? couriersList.map((c) => ({
                  label: c.courier_name,
                  value: c.courier_id,
                }))
              : key === 'sensor' || isSensorSlotProviderNameKey(key)
                ? sensorsList.map((s) => ({
                    label: s.sensor_name,
                    value: s.sensor_id,
                  }))
                : key === 'products'
                  ? productList.map((p) => ({
                      label: p.name,
                      value: p.id,
                    }))
                  : ['from_location', 'to_location'].includes(key)
                    ? locationList.map((l) => ({
                        label: l.location_name || l.name || l.sensor_name,
                        value: l.id,
                      }))
                    : key === 'teams'
                      ? allTeams.map((t) => ({
                          label: t.name,
                          value: t.id,
                          disabled:
                            t.permissions.is_create === true ||
                            t.permissions.is_update === true
                              ? false
                              : true,
                        }))
                      : [];

          const isSelectField =
            [
              'courier',
              'sensor',
              'products',
              'from_location',
              'to_location',
              'teams',
            ].includes(key) || isSensorSlotProviderNameKey(key);

          const onTextFieldFocus = () =>
            setCurrentErrorCellIndex(
              errorCells.findIndex(
                (c) => c.rowIndex === rowIndex && c.fieldKey === key
              )
            );

          const onPlainTextChange = (e) => {
            const input = e.target.value;
            const updated = [...data];

            if (key === 'link_code') {
              const isValid = /^[a-zA-Z0-9]{0,255}$/.test(input);
              updated[rowIndex][key] = {
                ...updated[rowIndex][key],
                value: input,
                error: isValid
                  ? ''
                  : 'Only alphanumeric characters (max 255) allowed',
              };
            } else {
              updated[rowIndex][key] = {
                ...updated[rowIndex][key],
                value: input,
                error: validateField(key, input),
              };

              if (
                key === 'courier_tracking_id' &&
                updated[rowIndex]['courier']?.value
              ) {
                updated[rowIndex][key].error = input.trim()
                  ? ''
                  : 'Tracking ID is required when Transport Provider is set.';
              }

              if (
                key === 'sensor_tracking_id' &&
                updated[rowIndex]['sensor']?.value
              ) {
                updated[rowIndex][key].error = input.trim()
                  ? ''
                  : 'Sensor ID is required when Sensor Provider is set.';
              }

              if (
                /^sensor_slot_\d+_tracking_id$/.test(key) &&
                updated[rowIndex][key.replace(/_tracking_id$/, '_name')]?.value
              ) {
                updated[rowIndex][key].error = input.trim()
                  ? ''
                  : 'Sensor ID is required when Sensor Provider is set.';
              }
            }

            setData(updated);
          };

          return (
            <div
              className={`bulk-upload-cell-align bulk-upload-cell-align--field ${
                hasError
                  ? isFocused
                    ? 'bulk-upload-cell-align--focus-error'
                    : 'bulk-upload-cell-align--error'
                  : ''
              }`}
            >
              {hasError && isFocused && (
                <div className='bulk-cell-error-label'>{field?.error}</div>
              )}
              <BulkUploadWrapCellOuter active={isWrapTextCol}>
                <div
                  ref={isFocused && hasError ? focusedCellElRef : undefined}
                  className={`${className} bulk-cell-shell--field ${
                    isWrapTextCol ? '' : 'bulk-cell-shell--fixed-height'
                  }`}
                >
                  {isSelectField ? (
                    key === 'products' ? (
                      <>
                        <Select
                          suffixIcon={null}
                          {...selectCommon}
                          mode='multiple'
                          options={selectOptions}
                          value={data[rowIndex][key]?.value || []}
                          labelInValue
                          virtual={false}
                          popupMatchSelectWidth={false}
                          getPopupContainer={bulkUploadPopupContainer}
                          classNames={{
                            popup: {
                              root: 'bulk-upload-product-select-dropdown',
                            },
                          }}
                          styles={{
                            popup: {
                              root: {
                                minWidth: 280,
                                maxWidth: 520,
                                zIndex: 10050,
                              },
                            },
                          }}
                          optionRender={(oriOption) => {
                            const data = oriOption.data;
                            const raw =
                              data?.label ?? oriOption.label ?? oriOption.value;
                            const text =
                              typeof raw === 'string' || typeof raw === 'number'
                                ? String(raw)
                                : String(oriOption.value ?? '');
                            return (
                              <span
                                className='bulk-upload-product-option-label'
                                title={text}
                              >
                                {text}
                              </span>
                            );
                          }}
                          ref={(node) => {
                            if (node) inputRefs.current[inputKey] = node;
                          }}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            opacity: 0,
                            zIndex: 2,
                            cursor: 'pointer',
                          }}
                          onFocus={() =>
                            setCurrentErrorCellIndex(
                              errorCells.findIndex(
                                (c) =>
                                  c.rowIndex === rowIndex && c.fieldKey === key
                              )
                            )
                          }
                          onChange={(value) => {
                            const updated = [...data];
                            updated[rowIndex][key] = {
                              ...updated[rowIndex][key],
                              value,
                              error: '',
                            };
                            setData(updated);
                          }}
                        />

                        <div
                          className='bulk-upload-products-preview'
                          onClick={() => inputRefs.current[inputKey]?.focus()}
                        >
                          <div
                            className='bulk-upload-line-clamp-2 bulk-upload-line-clamp-2--products'
                            title={(data[rowIndex][key]?.value || [])
                              .map((item) =>
                                typeof item === 'string' ? item : item.label
                              )
                              .join(', ')}
                            onClick={() => inputRefs.current[inputKey]?.focus()}
                          >
                            {(data[rowIndex][key]?.value || [])
                              .map((item) =>
                                typeof item === 'string' ? item : item.label
                              )
                              .join(', ')}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <Select
                          suffixIcon={null}
                          {...selectCommon}
                          mode={key === 'teams' ? 'multiple' : undefined}
                          options={selectOptions}
                          ref={(node) => {
                            if (node) inputRefs.current[inputKey] = node;
                          }}
                          styles={{
                            popup: {
                              root: {
                                zIndex: 9999,
                                ...(key === 'teams'
                                  ? { minWidth: 240, maxWidth: 360 }
                                  : {}),
                              },
                            },
                          }}
                          style={{
                            border: 'none',
                            width: '100%',
                            paddingInline: 0,
                          }}
                          popupMatchSelectWidth={key === 'teams' ? false : true}
                          onFocus={() =>
                            setCurrentErrorCellIndex(
                              errorCells.findIndex(
                                (c) =>
                                  c.rowIndex === rowIndex && c.fieldKey === key
                              )
                            )
                          }
                          onChange={(value) => {
                            const updated = [...data];

                            updated[rowIndex][key] = {
                              ...updated[rowIndex][key],
                              value,
                              error: validateField(key, value),
                            };

                            if (
                              key === 'from_location' ||
                              key === 'to_location'
                            ) {
                              const addrKey =
                                key === 'from_location'
                                  ? 'from_location_address'
                                  : 'to_location_address';
                              const rawId =
                                value != null &&
                                typeof value === 'object' &&
                                !Array.isArray(value) &&
                                'value' in value
                                  ? value.value
                                  : value;
                              const locRow = locationList.find(
                                (l) => String(l.id) === String(rawId ?? '')
                              );
                              const addr = locRow
                                ? String(locRow.address || '').trim()
                                : '';
                              updated[rowIndex][addrKey] = {
                                ...(updated[rowIndex][addrKey] || {
                                  value: '',
                                  error: '',
                                }),
                                value: addr,
                                error: '',
                              };
                            }

                            if (key === 'courier') {
                              const trackingId =
                                updated[rowIndex][
                                  'courier_tracking_id'
                                ]?.value?.trim();
                              updated[rowIndex]['courier_tracking_id'] = {
                                ...updated[rowIndex]['courier_tracking_id'],
                                error: trackingId
                                  ? ''
                                  : 'Tracking ID is required when Transport Provider is set.',
                              };
                            }

                            if (key === 'sensor') {
                              const sensorId =
                                updated[rowIndex][
                                  'sensor_tracking_id'
                                ]?.value?.trim();
                              updated[rowIndex]['sensor_tracking_id'] = {
                                ...updated[rowIndex]['sensor_tracking_id'],
                                error: sensorId
                                  ? ''
                                  : 'Sensor ID is required when Sensor Provider is set.',
                              };
                            }

                            if (isSensorSlotProviderNameKey(key)) {
                              const tk = sensorSlotTrackingFieldKey(key);
                              if (tk && updated[rowIndex][tk]) {
                                const raw = updated[rowIndex][tk]?.value;
                                const sensorId =
                                  typeof raw === 'string'
                                    ? raw.trim()
                                    : raw != null
                                      ? String(raw).trim()
                                      : '';
                                updated[rowIndex][tk] = {
                                  ...updated[rowIndex][tk],
                                  error: sensorId
                                    ? ''
                                    : 'Sensor ID is required when Sensor Provider is set.',
                                };
                              }
                            }

                            setData(updated);
                          }}
                          {...(key === 'teams' && {
                            tagRender: customTagRender,
                            maxTagPlaceholder: teamsMaxTagPlaceholder,
                            labelInValue: true,
                            maxTagCount: 'responsive',
                          })}
                        />
                      </>
                    )
                  ) : isWrapTextCol ? (
                    <Input.TextArea
                      ref={(el) => (inputRefs.current[inputKey] = el)}
                      size='small'
                      autoSize={{ minRows: 1, maxRows: 2 }}
                      value={field?.value}
                      onFocus={onTextFieldFocus}
                      onChange={onPlainTextChange}
                      style={{
                        width: '100%',
                        border: 'none',
                        backgroundColor: 'transparent',
                      }}
                    />
                  ) : (
                    <Input
                      ref={(el) => (inputRefs.current[inputKey] = el)}
                      size='small'
                      value={field?.value}
                      onFocus={onTextFieldFocus}
                      onChange={onPlainTextChange}
                      style={{
                        width: '100%',
                        border: 'none',
                        backgroundColor: 'transparent',
                      }}
                    />
                  )}
                </div>
              </BulkUploadWrapCellOuter>
            </div>
          );
        },
      };
    }),
  ];

  const getModalTitle = (editKey) => {
    if (!editKey) return '';
    const key = editKey.toLowerCase();

    const slotProvider = key.match(/^sensor_slot_(\d+)_name$/);
    if (slotProvider) {
      return `${themeT('sensor', theme.themeType)} provider ${slotProvider[1]}`;
    }

    const titleMap = {
      team: `${themeT('team', theme.themeType).toLowerCase()}s`,
      teams: `${themeT('team', theme.themeType).toLowerCase()}s`,
      courier: `${themeT('transportProvider', theme.themeType).toLowerCase()}s`,
    };

    return titleMap[key] || editKey.replace(/_/g, ' ');
  };

  return (
    <div className='bulk-upload-editor'>
      <CustomTitle
        text='Bulk upload'
        fontSize='20px'
        lineHeight='28px'
        fontWeight='700'
        marginBottom='4px'
      />

      {editModalOpen && (
        <CustomModalWithoutButton
          modalOpen={editModalOpen}
          setModalOpen={setEditModalOpen}
          fontSize='20px'
          modalTextFontSize='14px'
          modalTitle={`Bulk assign ${getModalTitle(editingFieldKey)}`}
          modalSize={447}
        >
          <BulkFieldSelector
            editingFieldKey={editingFieldKey}
            bulkFieldValue={bulkFieldValue}
            setBulkFieldValue={setBulkFieldValue}
            couriersList={couriersList}
            sensorsList={sensorsList}
            productList={productList}
            allTeams={allTeams}
            locationList={locationList}
            setEditModalOpen={setEditModalOpen}
            handleSave={handleSave}
          />
        </CustomModalWithoutButton>
      )}

      <div className='bulk-upload-editor-main' ref={mainContainerRef}>
        {getErrorCount() > 0 && <ErrorAlert />}

        <Table
          dataSource={data}
          columns={columns}
          rowKey={(row) => row.key}
          pagination={false}
          scroll={{
            x: 'max-content',
            y: tableScrollY,
          }}
        />
      </div>

      <BulkUploadFooter
        unauthorize={unauthorize}
        token={token}
        currentErrorCellIndex={currentErrorCellIndex}
        errorCells={errorCells}
        goToError={goToError}
        onBackToStep1={onBackToStep1}
        shipmentCount={shipmentCount}
        totalShipments={data.filter(isRowValid).length}
        totalRecords={data.length}
        onCancel={onCancel}
        data={data}
        isRowValid={isRowValid}
        prepareCleanedPayload={prepareCleanedPayload}
        apiResponse={fileData}
        showSpin={showSpin}
        hideSpin={hideSpin}
      />
    </div>
  );
}
